"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthUser } from "@/modules/auth";
import { getOrderDetails, getOrderHistory } from "../services/order.service";
import { detailsToCard, summaryToCard } from "../lib/order-card";
import type { OrderCard } from "../types/orders";

type Status = "loading" | "ready" | "error";

/**
 * Página do histórico — pequena de propósito: o histórico vem MAGRO do back
 * (sem itens/total) e cada card é hidratado com 1 GET de detalhes.
 */
const PAGE_SIZE = 10;
/** Detalhes buscados em paralelo por vez durante a hidratação. */
const HYDRATE_CONCURRENCY = 3;
/**
 * Meus pedidos do cliente logado.
 *
 * A lista vem do histórico paginado (cards magros) e cada card é HIDRATADO em
 * seguida com os detalhes — {@link HYDRATE_CONCURRENCY} por vez, atualizando o
 * estado conforme chegam (a UI troca skeleton por imagens/total sem esperar o
 * lote inteiro). Detalhes ficam em cache por orderId, com dedupe de chamadas em
 * voo — abrir um pedido já hidratado é instantâneo e nunca refaz o GET.
 */
export function useOrders({ pageSize = PAGE_SIZE }: { pageSize?: number } = {}) {
    const cpfCnpj = useAuthUser()?.cpfCnpj ?? null;
    const [cards, setCards] = useState<OrderCard[]>([]);
    const [status, setStatus] = useState<Status>("loading");
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const pageRef = useRef(1);
    /** Geração ativa — invalida respostas de um usuário/reload anterior. */
    const generationRef = useRef(0);
    const detailsCache = useRef(new Map<string, OrderCard>());
    const inflight = useRef(new Map<string, Promise<OrderCard>>());

    /** Detalhes completos de um pedido, com cache + dedupe de chamadas em voo. */
    const getDetails = useCallback(
        (orderId: string): Promise<OrderCard> => {
            if (!cpfCnpj) return Promise.reject(new Error("Sem usuário autenticado."));
            const cached = detailsCache.current.get(orderId);
            if (cached) return Promise.resolve(cached);
            const pending = inflight.current.get(orderId);
            if (pending) return pending;
            const promise = getOrderDetails(cpfCnpj, Number(orderId))
                .then((details) => {
                    const card = detailsToCard(details);
                    detailsCache.current.set(orderId, card);
                    return card;
                })
                .finally(() => {
                    inflight.current.delete(orderId);
                });
            inflight.current.set(orderId, promise);
            return promise;
        },
        [cpfCnpj]
    );

    /** Hidrata cards magros, N por vez, mesclando no estado conforme resolvem. */
    const hydrate = useCallback(
        async (batch: OrderCard[], generation: number) => {
            const queue = [...batch];
            async function worker() {
                for (let thin = queue.shift(); thin; thin = queue.shift()) {
                    const id = thin.id;
                    try {
                        const full = await getDetails(id);
                        if (generationRef.current !== generation) return;
                        setCards((prev) => prev.map((c) => (c.id === id ? full : c)));
                    } catch {
                        if (generationRef.current !== generation) return;
                        // Sem detalhes o card para de "carregar" mas segue
                        // abrível — a falha reaparece (com toast) ao abrir.
                        setCards((prev) =>
                            prev.map((c) => (c.id === id ? { ...c, hydrated: true } : c))
                        );
                    }
                }
            }
            await Promise.all(
                Array.from({ length: Math.min(HYDRATE_CONCURRENCY, queue.length) }, worker)
            );
        },
        [getDetails]
    );

    const loadPage = useCallback(
        async (page: number, generation: number) => {
            if (!cpfCnpj) return;
            const history = await getOrderHistory(cpfCnpj, { page, limit: pageSize });
            if (generationRef.current !== generation) return;
            const fresh = history.orders
                .map(summaryToCard)
                // Hidratados numa visita anterior à página reaproveitam o cache.
                .map((card) => detailsCache.current.get(card.id) ?? card);
            setCards((prev) => (page === 1 ? fresh : [...prev, ...fresh]));
            // `totalPages` é o total real do cliente. Sem ele (API antiga),
            // página cheia = provavelmente tem mais.
            setHasMore(
                typeof history.totalPages === "number"
                    ? page < history.totalPages
                    : history.orders.length === pageSize
            );
            pageRef.current = page;
            void hydrate(
                fresh.filter((c) => !c.hydrated),
                generation
            );
        },
        [cpfCnpj, hydrate, pageSize]
    );

    /** Primeira página + transição de status ao terminar. */
    const loadFirstPage = useCallback(
        (generation: number) => {
            loadPage(1, generation)
                .then(() => {
                    if (generationRef.current === generation) setStatus("ready");
                })
                .catch(() => {
                    if (generationRef.current === generation) setStatus("error");
                });
        },
        [loadPage]
    );

    // O reset síncrono fica de fora do effect (os estados iniciais já são
    // "loading"/[]); o effect só agenda o carregamento — quem reseta é o
    // reload, chamado de evento de UI.
    useEffect(() => {
        if (!cpfCnpj) return;
        loadFirstPage(++generationRef.current);
    }, [cpfCnpj, loadFirstPage]);

    /** Recomeça do zero (ex.: botão "Tentar novamente"). */
    function reload() {
        setStatus("loading");
        setCards([]);
        loadFirstPage(++generationRef.current);
    }

    /** Próxima página do histórico, preservando as já exibidas. */
    async function loadMore(): Promise<void> {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            await loadPage(pageRef.current + 1, generationRef.current);
        } finally {
            setLoadingMore(false);
        }
    }

    // "Pedir novamente" mora em `useRepeatOrder` — o mesmo fluxo serve o
    // header ("Refazer última compra") sem carregar o histórico inteiro.
    return { cards, status, hasMore, loadingMore, loadMore, reload, getDetails };
}
